import { useState, useEffect, useRef, useCallback } from 'react';
import { modules as DEFAULT_MODULES, ModuleName } from '@/constants';
import Modal from '@/common/Modal';
import ResetIcon from '@/assets/icons/reset.svg?react';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import { postSaveSubnavOrder } from '@/api/endpoints';
import { getUser, setUser } from '@/library/UserSessionHelper';
import { UserSettingsHelper } from '@/library';
import { ITourPageSettings } from '@/library/TourPageSettingsHelper';
import styles from './EditNavModal.module.scss';

interface EditNavModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleOrder: ModuleName[];
  // eslint-disable-next-line no-unused-vars
  onSave: (newOrder: ModuleName[]) => void;
  tourPageSettings?: ITourPageSettings;
}

function EditNavModal({ isOpen, onClose, moduleOrder, onSave, tourPageSettings }: EditNavModalProps) {
  const [workingOrder, setWorkingOrder] = useState<ModuleName[]>(moduleOrder);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  // Sync state when modal opens
  useEffect(() => {
    if (isOpen) {
      setWorkingOrder(moduleOrder);
      setDraggedIndex(null);
    }
  }, [isOpen, moduleOrder]);

  const handleReorder = useCallback(
    (toIndex: number) => {
      setWorkingOrder((prev) => {
        if (draggedIndex === null || toIndex === draggedIndex) return prev;
        const updated = [...prev];
        const [removed] = updated.splice(draggedIndex, 1);
        updated.splice(toIndex, 0, removed);
        setDraggedIndex(toIndex);
        return updated;
      });
    },
    [draggedIndex],
  );

  // Manage pointermove listener to support live reordering without native DnD ghost images
  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl || draggedIndex === null) return;

    const onPointerMove = (e: PointerEvent) => {
      const elementUnder = document.elementFromPoint(e.clientX, e.clientY);

      // Ensure we are still within the list boundaries
      if (!elementUnder || !listEl.contains(elementUnder)) return;

      const listItem = elementUnder.closest(`.${styles.dragItem}`) as HTMLElement | null;
      const index = listItem?.dataset.index ? Number(listItem.dataset.index) : null;

      if (index !== null) handleReorder(index);
    };

    const onPointerUp = () => setDraggedIndex(null);

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [draggedIndex, handleReorder]);

  const handleUpdate = async () => {
    setShowSpinner(true);
    try {
      const user = await getUser();
      if (user && user.appUserId) {
        const subnavOrder = JSON.stringify({ subnav: workingOrder.join('|') });
        await postSaveSubnavOrder(user.appUserId, subnavOrder);

        // Update IndexedDB user
        user.subnavOrder = subnavOrder;
        await setUser(user);
      }
      onSave(workingOrder);
      onClose();
    } catch (error) {
      console.error('Failed to save subnav order:', error);
    } finally {
      setShowSpinner(false);
    }
  };

  const handleCancel = () => {
    setWorkingOrder(moduleOrder);
    onClose();
  };

  const handleReset = () => {
    // Always set online to true since this functionality is only available when online
    const availableDefaults = UserSettingsHelper.getAvailableModules([...DEFAULT_MODULES], true, tourPageSettings);
    setWorkingOrder(availableDefaults);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} className={styles.editNavModal}>
      {showSpinner && <SpinnerOverlay />}
      <div className={styles.headerRow}>
        <p className={styles.title}>Hold &amp; Drag to Re-Order Nav</p>
        <button className={styles.resetButton} onClick={handleReset} title="Reset to default">
          <ResetIcon width={16} height={16} strokeWidth={2.5} />
        </button>
      </div>
      <ul className={styles.dragList} ref={listRef}>
        {workingOrder.map((moduleName, index) => (
          <li key={moduleName} data-index={index} className={`${styles.dragItem} ${draggedIndex === index ? styles.dragItemDragging : ''}`} onPointerDown={() => setDraggedIndex(index)}>
            <span className={styles.gripIcon}>☰</span>
            <span className={styles.label}>{moduleName}</span>
          </li>
        ))}
      </ul>
      <div className={styles.buttonRow}>
        <button className={styles.cancelButton} onClick={handleCancel}>
          Cancel
        </button>
        <button className={styles.updateButton} onClick={handleUpdate}>
          Update
        </button>
      </div>
    </Modal>
  );
}

export default EditNavModal;
